import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ListingSummary, SearchOptions } from '@/types'

import Link from 'next/link'
import { Heart, Sparkles } from 'lucide-react'
import { getCatalog, getSearchOptions } from '@/lib/catalog'
import { plural } from '@/lib/format'
import { emptySearchOptions } from '@/lib/search'
import { useSavedHomes } from '@/hooks/use-saved-homes'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ListingCard } from '@/components/homes/listing-card'

interface SavedPageProps {
    listings: ListingSummary[]
    searchOptions: SearchOptions
}

const SavedPage: FC<Partial<SavedPageProps>> = ({ listings = [], searchOptions = emptySearchOptions }): ReactNode => {
    const { ids, ready } = useSavedHomes()
    const saved = ids
        .map(id => listings.find(listing => listing.id === id))
        .filter((listing): listing is ListingSummary => Boolean(listing))

    return (
        <Layout searchOptions={searchOptions}>
            <Seo title='Saved homes' description='The homes you saved with the heart button, kept on this device.' />

            <div className='mx-auto min-h-[60vh] max-w-[1760px] px-4 py-10 sm:px-6 lg:px-10'>
                <h1 className='font-heading text-3xl font-bold tracking-tight sm:text-4xl'>Saved homes</h1>
                <p className='mt-2 text-muted-foreground' aria-live='polite'>
                    {ready && saved.length > 0
                        ? <><span>{saved.length}</span> {plural(saved.length, 'home')} saved on this device</>
                        : 'Tap the heart on any home to keep it here.'}
                </p>

                {!ready && (
                    <ul className='mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' aria-hidden='true'>
                        {Array.from({ length: 4 }, (_, index) => (
                            <li key={index}>
                                <div className='aspect-[20/19] animate-pulse rounded-xl bg-muted' />
                                <div className='mt-3 h-4 w-2/3 animate-pulse rounded bg-muted' />
                                <div className='mt-2 h-4 w-1/3 animate-pulse rounded bg-muted' />
                            </li>
                        ))}
                    </ul>
                )}

                {ready && saved.length > 0 && (
                    <ul className='mt-8 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                        {saved.map(listing => (
                            <li key={listing.id}>
                                <ListingCard listing={listing} />
                            </li>
                        ))}
                    </ul>
                )}

                {ready && saved.length === 0 && (
                    <section className='mt-10 rounded-3xl border border-border px-6 py-16 text-center'>
                        <span className='mx-auto flex size-16 items-center justify-center rounded-full bg-brand-soft text-primary'>
                            <Heart className='size-8' aria-hidden='true' />
                        </span>
                        <h2 className='mt-5 text-xl font-semibold'>No saved homes yet</h2>
                        <p className='mx-auto mt-2 max-w-md text-muted-foreground'>
                            As you browse, tap the heart on any photo. Your favorites stay here on this device, no account needed.
                        </p>

                        <div className='mt-7 flex flex-wrap justify-center gap-3'>
                            <Link href='/homes' className='flex h-12 items-center rounded-xl bg-foreground px-6 font-semibold text-background'>
                                Browse homes
                            </Link>
                            <Link href='/style-match' className='flex h-12 items-center gap-2 rounded-xl border border-foreground px-6 font-semibold'>
                                <Sparkles className='size-4' aria-hidden='true' />
                                Try Style match
                            </Link>
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<SavedPageProps> = async () => {
    try {
        const catalog = await getCatalog()

        return {
            props: { listings: catalog.listings, searchOptions: getSearchOptions(catalog) },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Saved homes data failed to load:', error instanceof Error ? error.message : error)

        return {
            props: { listings: [], searchOptions: emptySearchOptions },
            revalidate: 10
        }
    }
}

export default SavedPage
